(() => {
if (customElements.get('rdb-custom-bouquet-builder')) return;
class RdbCustomBouquetBuilder extends HTMLElement {
  t(key, values = {}) {
    const text = this.strings?.[key] || '';
    return text.replace(/{{\s*(\w+)\s*}}|__(\w+)__/g, (_, name, token) => String(values[name || token] ?? ''));
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') { this.bindCurrencyPrices(); return; }
    if (this.initializeFrame) return;

    this.initializeFrame = requestAnimationFrame(() => {
      this.initializeFrame = null;
      if (this.isConnected) this.initialize();
    });
  }

  disconnectedCallback() {
    this.currencyAbort?.abort();
    cancelAnimationFrame(this.priceFrame);
    if (this.initializeFrame) cancelAnimationFrame(this.initializeFrame);
    this.initializeFrame = null;
  }

  initialize() {
    if (this.dataset.initialized === 'true') return;

    this.strings = JSON.parse(this.querySelector('[data-builder-strings]')?.textContent || '{}');
    this.cartStatus = 'idle';
    this.catalogStatus = 'loading';
    this.restored = false;
    window.render_builder_to_text = () => this.snapshot();
    this.form = this.querySelector('[data-builder-form]');
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.progress = [...this.querySelectorAll('.ros-builder__progress li')];
    this.cards = [...this.querySelectorAll('[data-flower-card]')];
    this.next = this.querySelector('[data-next]');
    this.back = this.querySelector('[data-back]');
    this.checkoutButton = this.querySelector('[data-checkout]');
    this.previewList = this.querySelector('[data-preview-list]');
    this.summaryEmpty = this.querySelector('[data-summary-empty]');
    this.selectedCount = this.querySelector('[data-selected-count]');
    this.status = this.querySelector('.ros-builder__status');
    this.totalElement = this.querySelector('[data-total]');
    this.mobileTotalElement = this.querySelector('[data-total-mobile]');
    this.error = this.querySelector('[data-error]');
    this.message = this.querySelector('[data-message]');
    this.messageCount = this.querySelector('[data-message-count]');
    this.from = this.querySelector('[data-from]');
    this.deliveryDate = this.querySelector('[data-date]');
    this.anonymous = this.querySelector('[data-anonymous]');
    this.current = 0;
    this.min = Number(this.dataset.min) || 3;
    this.max = Math.max(this.min, Number(this.dataset.max) || 8);
    this.maxStems = Number(this.dataset.maxStems) || 25;
    this.currency = this.dataset.currency || 'AED';

    const requiredElements = [
      this.form,
      this.next,
      this.back,
      this.checkoutButton,
      this.previewList,
      this.status,
      this.totalElement,
      this.error,
    ];

    if (requiredElements.some((element) => !element) || this.steps.length !== 4) {
      console.error('Custom bouquet builder could not initialize because required markup is missing.');
      return;
    }

    this.dataset.initialized = 'true';
    this.bindCurrencyPrices();
    this.form.addEventListener('submit', (event) => event.preventDefault());

    this.cards.forEach((card) => {
      const select = card.querySelector('[data-flower-select]');
      const plus = card.querySelector('[data-qty-plus]');
      const minus = card.querySelector('[data-qty-minus]');

      if (!select || !plus || !minus || !card.querySelector('[data-qty]')) return;

      select.addEventListener('click', () => {
        if (!this.isAvailable(card)) return;
        this.setQuantity(card, this.getQuantity(card) > 0 ? 0 : 1);
      });
      plus.addEventListener('click', () => this.setQuantity(card, this.getQuantity(card) + 1));
      minus.addEventListener('click', () => this.setQuantity(card, this.getQuantity(card) - 1));
    });

    this.querySelector('[data-base-variant]')?.addEventListener('change', () => this.update());
    this.querySelectorAll('input[name="palette"], input[name="presentation"]').forEach((input) => {
      input.addEventListener('change', () => this.update());
    });

    [this.message, this.from, this.deliveryDate].forEach((field) => {
      field?.addEventListener('input', () => this.update());
      field?.addEventListener('change', () => this.update());
    });

    this.anonymous?.addEventListener('change', () => {
      this.updateAnonymousState();
      this.update();
    });

    this.next.addEventListener('click', () => this.move(1));
    this.back.addEventListener('click', () => this.move(-1));
    this.checkoutButton.addEventListener('click', () => this.checkout());

    if (this.deliveryDate) {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Dubai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const datePart = (type) => parts.find((part) => part.type === type)?.value || '';
      this.deliveryDate.min = `${datePart('year')}-${datePart('month')}-${datePart('day')}`;
    }

    this.restoreState();
    this.updateAnonymousState();
    this.showStep(0, { focus: false, scroll: false });
    this.update({ persist: false });
    this.resolveCatalogProducts();
  }

  isAvailable(card) {
    return card.dataset.available === 'true';
  }

  productUrl(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const prefix = root.endsWith('/') ? root : `${root}/`;
    return `${prefix}products/${encodeURIComponent(handle)}.js`;
  }

  normalizeOption(value) {
    return String(value || '')
      .normalize('NFKC')
      .replace(/[’‘]/g, "'")
      .trim()
      .toLowerCase();
  }

  async fetchProduct(handle) {
    const response = await fetch(this.productUrl(handle), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Product ${handle} could not be loaded.`);
    return response.json();
  }

  async resolveCatalogProducts() {
    const unresolvedCards = this.cards.filter((card) => !card.dataset.variantId);
    const baseSelect = this.querySelector('[data-base-variant]');
    const needsBase = Boolean(baseSelect && ![...baseSelect.options].some((option) => option.value));
    const flowerHandle = this.dataset.flowerCatalogHandle;
    const baseHandle = this.dataset.baseProductHandle;

    if (!unresolvedCards.length && !needsBase) { this.catalogStatus = 'ready'; return; }

    const [flowerResult, baseResult] = await Promise.allSettled([
      unresolvedCards.length && flowerHandle ? this.fetchProduct(flowerHandle) : Promise.resolve(null),
      needsBase && baseHandle ? this.fetchProduct(baseHandle) : Promise.resolve(null),
    ]);

    if (unresolvedCards.length) {
      const variants = flowerResult.status === 'fulfilled' ? flowerResult.value?.variants || [] : [];
      unresolvedCards.forEach((card) => {
        const configuredId = String(card.dataset.catalogVariantId || '').trim();
        const optionName = this.normalizeOption(card.dataset.catalogOption || card.dataset.name);
        const variant = variants.find((candidate) => {
          // Stable catalogue identity survives native option-name translations.
          // An obsolete configured ID must remain unavailable, never name-match.
          if (configuredId) return String(candidate.id) === configuredId;
          const candidateName = candidate.option1 || candidate.options?.[0] || candidate.title;
          return this.normalizeOption(candidateName) === optionName;
        });
        const available = Boolean(variant?.available);
        card.dataset.variantId = variant?.id ? String(variant.id) : '';
        if (variant?.price != null) card.dataset.price = String(Number(variant.price));
        card.dataset.available = String(available);
        card.classList.remove('is-loading');
        card.classList.toggle('is-unavailable', !available);

        const availability = card.querySelector('[data-flower-availability]');
        if (availability) availability.textContent = available ? '' : this.t('unavailable');
      });
    }

    if (needsBase && baseSelect) {
      const status = this.querySelector('[data-base-setup-status]');
      const variants = baseResult.status === 'fulfilled' ? baseResult.value?.variants || [] : [];
      baseSelect.replaceChildren();

      variants.forEach((variant) => {
        const option = document.createElement('option');
        const isDefaultVariant = this.normalizeOption(variant.title) === 'default title';
        option.value = String(variant.id);
        option.dataset.price = String(Number(variant.price));
        option.dataset.priceTitle = variant.title;
        option.dataset.priceIncluded = String(isDefaultVariant && Number(variant.price) === 0);
        option.disabled = !variant.available;
        option.textContent = isDefaultVariant && Number(variant.price) === 0
          ? `${this.t('custom_bouquet')} — ${this.t('included')}${variant.available ? '' : ` — ${this.t('sold_out')}`}`
          : `${variant.title} — ${this.money(Number(variant.price))}${variant.available ? '' : ` — ${this.t('sold_out')}`}`;
        baseSelect.append(option);
      });

      const firstAvailable = [...baseSelect.options].find((option) => !option.disabled);
      baseSelect.disabled = !firstAvailable;
      if (firstAvailable) {
        baseSelect.value = firstAvailable.value;
        if (status) status.textContent = '';
      } else {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = this.t('checkout_unavailable');
        baseSelect.append(option);
        if (status) status.textContent = this.t('catalog_unavailable');
      }
    }

    this.catalogStatus = this.cards.some(card => this.isAvailable(card)) && this.querySelector('[data-base-variant]')?.value ? 'ready' : 'unavailable';
    this.restoreState();
    this.update({ persist: false });
  }

  getQuantity(card) {
    const output = card.querySelector('[data-qty]');
    return Math.max(0, Number(output?.value || output?.textContent || 0));
  }

  writeQuantity(card, quantity) {
    const output = card.querySelector('[data-qty]');
    if (!output) return;
    output.value = String(quantity);
    output.textContent = String(quantity);
  }

  selectedCards() {
    return this.cards.filter((card) => this.getQuantity(card) > 0);
  }

  selectedStemCount() {
    return this.selectedCards().reduce((total, card) => total + this.getQuantity(card), 0);
  }

  setQuantity(card, requestedQuantity) {
    if (!this.isAvailable(card)) return;

    const previousQuantity = this.getQuantity(card);
    const quantity = Math.max(0, Math.min(this.maxStems, Math.floor(Number(requestedQuantity) || 0)));

    if (quantity > 0 && previousQuantity === 0 && this.selectedCards().length >= this.max) {
      this.status.textContent = this.t('maximum_types', { max: this.max });
      return;
    }

    this.writeQuantity(card, quantity);
    this.update();
  }

  bindCurrencyPrices() {
    this.currencyAbort?.abort();
    this.currencyAbort = new AbortController();
    const refresh = () => {
      cancelAnimationFrame(this.priceFrame);
      this.priceFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.displayPrices();
      });
    };
    for (const event of ['rdb:currencychange', 'futureblink:currencychange']) {
      document.addEventListener(event, refresh, { signal: this.currencyAbort.signal });
    }
    refresh();
  }

  priceText(element, value, suffix = '') {
    if (!element) return;
    if (element.textContent === value + suffix && element.firstElementChild?.tagName === 'BDI') return;
    const price = document.createElement('bdi');
    price.dir = 'auto';
    price.textContent = value;
    element.replaceChildren(price, document.createTextNode(suffix));
  }

  displayPrices() {
    this.cards.forEach(card => {
      this.priceText(card.querySelector('[data-flower-price]'), this.money(Number(card.dataset.price) || 0), ` / ${this.t(`unit_${card.dataset.unit}`)}`);
    });
    const base = this.querySelector('[data-base-variant]');
    for (const option of base?.options || []) {
      if (!option.value || option.dataset.priceTitle == null) continue;
      const label = option.dataset.priceIncluded === 'true'
        ? `${this.t('custom_bouquet')} — ${this.t('included')}`
        : `${option.dataset.priceTitle} — \u2066${this.money(Number(option.dataset.price))}\u2069`;
      option.textContent = label + (option.disabled ? ` — ${this.t('sold_out')}` : '');
    }
    const total = this.money(this.total());
    this.priceText(this.totalElement, total);
    this.priceText(this.mobileTotalElement, total);
    this.priceText(this.querySelector('[data-review-price]'), total);
    const reviewBase = this.querySelector('[data-review-base]');
    if (reviewBase && base?.selectedOptions[0]) reviewBase.textContent = base.selectedOptions[0].textContent.trim();
  }

  money(cents) {
    const converted = window.rdbCurrencyPrices?.format(cents, this.currency);
    if (converted) return converted;
    try {
      const locale = this.dataset.locale === 'ar' ? 'ar-AE' : this.dataset.locale === 'de' ? 'de-DE' : 'en-AE';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return `AED ${(cents / 100).toFixed(2)}`;
    }
  }

  total() {
    const basePrice = Number(this.querySelector('[data-base-variant]')?.selectedOptions[0]?.dataset.price || 0);

    return this.selectedCards().reduce((total, card) => {
      return total + (Number(card.dataset.price) || 0) * this.getQuantity(card);
    }, basePrice);
  }

  update(options = {}) {
    const { persist = true } = options;
    const selected = this.selectedCards();
    const count = selected.length;
    const stems = this.selectedStemCount();

    this.cards.forEach((card) => this.refreshCard(card, count));

    if (count < this.min) {
      const remaining = this.min - count;
      this.status.textContent = this.t(remaining === 1 ? 'remaining_one' : 'remaining_other', { count, remaining });
    } else if (count >= this.max) {
      this.status.textContent = this.t('maximum_reached', { count });
    } else {
      this.status.textContent = this.t('selection_status', { count, max: this.max, stems });
    }

    if (this.selectedCount) {
      this.selectedCount.textContent = count < this.min
        ? this.t('selected_minimum', { count, min: this.min })
        : this.t('selected_summary', { count, stems });
    }

    this.displayPrices();
    this.updateMessageCounter();
    this.renderPreview();
    this.updateNavigation();

    if (this.current === 3) this.review();
    if (persist) this.saveState();

    this.dispatchEvent(
      new CustomEvent('bouquet:change', {
        bubbles: true,
        detail: { selectedTypes: count, selectedItems: stems, total: this.total(), step: this.current + 1 },
      })
    );
  }

  refreshCard(card, selectedCount) {
    const quantity = this.getQuantity(card);
    const isSelected = quantity > 0;
    const isAvailable = this.isAvailable(card);
    const atVarietyLimit = selectedCount >= this.max && !isSelected;
    const isPending = card.dataset.available === 'pending';
    const select = card.querySelector('[data-flower-select]');
    const plus = card.querySelector('[data-qty-plus]');
    const minus = card.querySelector('[data-qty-minus]');

    card.classList.toggle('is-selected', isSelected);
    card.classList.toggle('is-loading', isPending);
    card.classList.toggle('is-unavailable', !isAvailable && !isPending);
    select?.setAttribute('aria-pressed', String(isSelected));
    if (select) {
      select.disabled = !isAvailable || atVarietyLimit;
      select.setAttribute(
        'aria-label',
        isSelected
          ? this.t('remove_flower', { name: card.dataset.name })
          : this.t('select_flower', { name: card.dataset.name })
      );
    }
    if (minus) minus.disabled = !isAvailable || quantity <= 0;
    if (plus) plus.disabled = !isAvailable || quantity >= this.maxStems || atVarietyLimit;
  }

  renderPreview() {
    const selected = this.selectedCards();
    this.previewList?.replaceChildren();
    if (this.summaryEmpty) this.summaryEmpty.hidden = selected.length > 0;

    if (!selected.length) return;

    selected.forEach((card) => {
      if (!this.previewList) return;
      const item = document.createElement('li');
      const name = document.createElement('span');
      const quantity = document.createElement('strong');
      name.textContent = card.dataset.name;
      quantity.textContent = `× ${this.getQuantity(card)}`;
      item.append(name, quantity);
      this.previewList.append(item);
    });
  }

  updateNavigation() {
    const selectedCount = this.selectedCards().length;
    const selectedBaseOption = this.querySelector('[data-base-variant]')?.selectedOptions[0];
    const hasBaseVariant = Boolean(selectedBaseOption?.value && !selectedBaseOption.disabled);

    this.back.hidden = this.current === 0;
    this.next.hidden = this.current === 3;
    this.next.disabled =
      (this.current === 0 && selectedCount < this.min) ||
      (this.current === 1 && !hasBaseVariant);
  }

  move(direction) {
    if (direction > 0 && this.next.disabled) return;
    if (direction > 0 && this.current === 2 && !this.hasAttribute('data-delivery-in-cart') && !this.deliveryDate?.value) {
      this.deliveryDate?.reportValidity();
      this.deliveryDate?.focus();
      return;
    }
    const nextStep = Math.max(0, Math.min(3, this.current + direction));
    if (nextStep === this.current) return;
    this.showStep(nextStep, { focus: true, scroll: true });
  }

  showStep(step, options = {}) {
    const { focus = true, scroll = true } = options;
    this.current = step;

    this.steps.forEach((section, index) => {
      section.hidden = index !== this.current;
      section.classList.toggle('is-active', index === this.current);
    });

    this.progress.forEach((item, index) => {
      item.classList.toggle('is-active', index <= this.current);
      if (index === this.current) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });

    if (this.current === 3) this.review();
    this.updateNavigation();

    if (scroll || focus) {
      const activeStep = this.steps[this.current];
      requestAnimationFrame(() => {
        if (!this.isConnected || this.steps[this.current] !== activeStep) return;
        const heading = activeStep?.querySelector('h2');
        if (focus) heading?.focus({ preventScroll: true });
        if (scroll) {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          (heading || activeStep)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start', inline: 'nearest' });
        }
      });
    }

    this.dispatchEvent(
      new CustomEvent('bouquet:step', { bubbles: true, detail: { step: this.current + 1 } })
    );
  }

  review() {
    const review = this.querySelector('[data-review]');
    if (!review) return;

    const baseVariant = this.querySelector('[data-base-variant]')?.selectedOptions[0]?.textContent?.trim() || this.t('not_configured');
    const flowers = this.selectedCards()
      .map((card) => `${card.dataset.name} × ${this.getQuantity(card)}`)
      .join(', ');
    const rows = [
      [this.t('flowers'), flowers],
      [this.t('bouquet_size'), baseVariant],
      [this.t('colour_direction'), this.querySelector('input[name="palette"]:checked')?.nextElementSibling?.textContent || ''],
      [this.t('presentation'), this.querySelector('input[name="presentation"]:checked')?.nextElementSibling?.textContent || ''],
      [this.t('card_message'), this.message?.value.trim() || this.t('no_message')],
      [this.t('from'), this.anonymous?.checked ? this.t('anonymous_value') : this.from?.value.trim() || this.t('not_specified')],
      ...(!this.hasAttribute('data-delivery-in-cart') ? [[this.t('delivery_date'), this.deliveryDate?.value || this.t('required_value')]] : []),
      [this.t('estimated_total'), this.money(this.total())],
    ];

    review.replaceChildren();
    rows.forEach(([label, value], index) => {
      const row = document.createElement('div');
      const heading = document.createElement('strong');
      const content = document.createElement('span');
      heading.textContent = label;
      content.textContent = value;
      if (index === rows.length - 1) {
        content.setAttribute('data-review-price', '');
        this.priceText(content, value);
      } else if (index === 1) {
        content.setAttribute('data-review-base', '');
        content.dir = 'auto';
      }
      row.append(heading, content);
      review.append(row);
    });
  }

  updateMessageCounter() {
    if (this.messageCount) this.messageCount.textContent = String(this.message?.value.length || 0);
  }

  updateAnonymousState() {
    if (!this.from || !this.anonymous) return;
    this.from.disabled = this.anonymous.checked;
    this.from.closest('.ros-field')?.classList.toggle('is-disabled', this.anonymous.checked);
  }

  stateKey() {
    return `rdb-custom-bouquet:v1:${window.location.pathname}`;
  }

  saveState() {
    if (window.Shopify?.designMode) return;

    const quantities = {};
    this.cards.forEach((card) => {
      const quantity = this.getQuantity(card);
      if (quantity > 0) quantities[card.dataset.key || card.dataset.catalogOption || card.dataset.name] = quantity;
    });

    const state = {
      quantities,
      baseVariant: this.querySelector('[data-base-variant]')?.value || '',
      palette: this.querySelector('input[name="palette"]:checked')?.value || '',
      presentation: this.querySelector('input[name="presentation"]:checked')?.value || '',
      anonymous: Boolean(this.anonymous?.checked),
    };

    try {
      sessionStorage.setItem(this.stateKey(), JSON.stringify(state));
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes; the builder remains fully usable.
    }
  }

  restoreState() {
    if (window.Shopify?.designMode) return;

    let state;
    try {
      state = JSON.parse(sessionStorage.getItem(this.stateKey()) || 'null');
    } catch (_error) {
      return;
    }
    if (!state || typeof state !== 'object') return;
    this.restored = true;

    let restoredTypes = 0;
    this.cards.forEach((card) => {
      const storedQuantity = Math.max(0, Math.min(this.maxStems, Math.floor(Number(state.quantities?.[card.dataset.key || card.dataset.catalogOption || card.dataset.name]) || 0)));
      if (this.isAvailable(card) && storedQuantity > 0 && restoredTypes < this.max) {
        this.writeQuantity(card, storedQuantity);
        restoredTypes += 1;
      }
    });

    const baseVariant = this.querySelector('[data-base-variant]');
    if (baseVariant && [...baseVariant.options].some((option) => option.value === state.baseVariant && !option.disabled)) {
      baseVariant.value = state.baseVariant;
    }

    const palette = [...this.querySelectorAll('input[name="palette"]')].find((input) => input.value === state.palette);
    const presentation = [...this.querySelectorAll('input[name="presentation"]')].find(
      (input) => input.value === state.presentation
    );
    if (palette) palette.checked = true;
    if (presentation) presentation.checked = true;
    if (this.anonymous) this.anonymous.checked = Boolean(state.anonymous);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
    this.error.focus({ preventScroll: false });
  }

  async checkout() {
    if (this.cartStatus === 'submitting' || this.cartStatus === 'succeeded') return;
    this.error.hidden = true;

    if (this.selectedCards().length < this.min) {
      this.showError(this.t('minimum_error', { min: this.min }));
      return;
    }

    const baseSelect = this.querySelector('[data-base-variant]');
    const baseOption = baseSelect?.selectedOptions[0];
    const baseVariant = baseSelect?.value;
    if (!baseVariant || baseOption?.disabled) {
      this.showError(this.t('catalog_unavailable'));
      return;
    }

    if (!this.hasAttribute('data-delivery-in-cart') && !this.deliveryDate?.value) {
      this.showError(this.t('date_error'));
      this.deliveryDate?.focus();
      return;
    }

    if (this.deliveryDate && this.deliveryDate.value < this.deliveryDate.min) {
      this.showError(this.t('past_date_error'));
      return;
    }

    const missingProducts = this.selectedCards().filter((card) => !card.dataset.variantId || !this.isAvailable(card));
    if (missingProducts.length) {
      this.showError(this.t('flowers_error'));
      return;
    }

    const bouquetGroup = `RB-${Date.now().toString(36)}`;
    const selectedBlooms = this.selectedCards()
      .map((card) => `${card.dataset.name} × ${this.getQuantity(card)}`)
      .join(', ');
    const properties = {
      [this.t('selected_blooms')]: selectedBlooms,
      [this.t('colour_direction')]: this.querySelector('input[name="palette"]:checked')?.nextElementSibling?.textContent || '',
      [this.t('presentation')]: this.querySelector('input[name="presentation"]:checked')?.nextElementSibling?.textContent || '',
      [this.t('card_message')]: this.message?.value || '',
      [this.t('card_from')]: this.anonymous?.checked ? '' : this.from?.value || '',
      ...(!this.hasAttribute('data-delivery-in-cart') ? { [this.t('delivery_date')]: this.deliveryDate.value } : {}),
      [this.t('anonymous')]: this.anonymous?.checked ? this.t('yes') : this.t('no'),
      '_Bouquet group': bouquetGroup,
    };
    const items = [
      { id: Number(baseVariant), quantity: 1, properties },
      ...this.selectedCards().map((card) => ({
        id: Number(card.dataset.variantId),
        quantity: this.getQuantity(card),
        properties: { '_Bouquet component': 'Yes', '_Bouquet group': bouquetGroup },
      })),
    ];

    this.cartStatus = 'submitting';
    const originalLabel = this.checkoutButton.textContent;
    this.checkoutButton.disabled = true;
    this.checkoutButton.setAttribute('aria-busy', 'true');
    this.checkoutButton.textContent = this.t('adding');

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const routePrefix = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
      const response = await fetch(`${routePrefix}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        throw new Error(this.t('cart_error'));
      }

      try {
        sessionStorage.removeItem(this.stateKey());
      } catch (_error) {
        // Ignore storage failures after a successful cart request.
      }
      this.cartStatus = 'succeeded';
      window.location.assign(`${routePrefix}cart`);
    } catch (error) {
      this.cartStatus = 'failed';
      this.showError(this.t('cart_error'));
      this.checkoutButton.disabled = false;
      this.checkoutButton.removeAttribute('aria-busy');
      this.checkoutButton.textContent = originalLabel;
    }
  }
  snapshot() {
    const selected = this.selectedCards().map(card => ({
      key: card.dataset.key || card.dataset.catalogOption,
      variantId: card.dataset.variantId,
      quantity: this.getQuantity(card),
      unitPriceMinor: Number(card.dataset.price),
      available: this.isAvailable(card),
    }));
    const base = this.querySelector('[data-base-variant]')?.selectedOptions[0];
    const reasons = [];
    if (selected.length < this.min) reasons.push('minimum_not_met');
    if (selected.length > this.max) reasons.push('maximum_reached');
    if (!base?.value || base.disabled) reasons.push('base_unavailable');
    if (!this.hasAttribute('data-delivery-in-cart') && !this.deliveryDate?.value) reasons.push('delivery_date_required');
    else if (this.deliveryDate && this.deliveryDate.value < this.deliveryDate.min) reasons.push('delivery_date_in_past');
    if (selected.some(item => !item.available || !item.variantId)) reasons.push('unavailable_component');
    const units = selected.reduce((sum, item) => sum + item.quantity, 0);
    return {
      version: 1, mode: this.cartStatus === 'submitting' ? 'submitting' : this.catalogStatus === 'loading' ? 'loading' : 'editing',
      activeCategory: 'flower', step: this.current + 1, selection: selected,
      composition: { kind: 'text_summary', slots: [] }, size: base?.value || '',
      totals: { selectedTypes: selected.length, selectedUnits: units, qualifyingStems: units,
        remainingCapacity: Math.max(0, this.max - selected.length), basePriceMinor: Number(base?.dataset.price || 0),
        componentPriceMinor: this.total() - Number(base?.dataset.price || 0), priceMinor: this.total(), currencyCode: this.currency },
      limits: { minimumTypes: this.min, maximumTypes: this.max, perComponentMaximum: this.maxStems },
      validation: { valid: reasons.length === 0, reasons },
      animation: { active: false, phase: null, reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches },
      persistence: { enabled: true, restored: this.restored, schemaVersion: 1, personalFieldsStored: false },
      cart: { status: this.cartStatus, lineCount: selected.length + 1 },
      shopifyPayload: { endpoint: `${window.Shopify?.routes?.root || '/'}cart/add.js`,
        grouping: 'flat_lines_with_private_group_property',
        items: base?.value ? [{ id: Number(base.value), quantity: 1 }, ...selected.map(item => ({ id: Number(item.variantId), quantity: item.quantity }))] : [] },
    };
  }

}

if (!customElements.get('rdb-custom-bouquet-builder')) {
  customElements.define('rdb-custom-bouquet-builder', RdbCustomBouquetBuilder);
}

})();
